import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  format,
  addMonths,
  subMonths,
  addWeeks,
  subWeeks,
  addDays,
  subDays,
  isSameMonth,
  isSameDay,
  isToday,
  parseISO,
  differenceInMinutes,
  setHours,
  setMinutes,
} from "date-fns";
import type { CourtPublic, CalendarEvent } from "@dugads/shared";
import { useAuth } from "@/lib/auth-context";
import * as api from "@/lib/api";
import { MatchScheduler } from "@/components/match-scheduler";
import {
  DndContext,
  DragOverlay,
  useDraggable,
  useDroppable,
  pointerWithin,
  type DragStartEvent,
  type DragEndEvent,
} from "@dnd-kit/core";
import { useDndSensors } from "@/lib/dnd-sensors";
import { SendMessageButton } from "@/components/send-message-button";
import { SchoolCrest } from "@/components/school-crest";
import { EventStatusBadge } from "@/components/events/event-status-badge";
import { useMediaQuery } from "@/lib/use-media-query";
import { MobileCalendar } from "@/components/mobile-calendar";
import { Button, buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  List,
  Calendar as CalendarIcon,
  X,
  Layers,
  ExternalLink,
  Pencil,
  Star,
  Trash2,
  PanelRightClose,
  PanelRightOpen,
  Clock,
  MapPin,
} from "lucide-react";

type ViewMode = "month" | "week" | "day" | "list";
type EventTypeFilter = "all" | "match_day" | "practice" | "tryout";

const EVENT_COLORS: Record<string, { bg: string; border: string; text: string; dot: string }> = {
  match_day: {
    bg: "bg-blue-100 dark:bg-blue-900/30",
    border: "border-blue-300 dark:border-blue-700",
    text: "text-blue-800 dark:text-blue-300",
    dot: "bg-blue-500",
  },
  practice: {
    bg: "bg-green-100 dark:bg-green-900/30",
    border: "border-green-300 dark:border-green-700",
    text: "text-green-800 dark:text-green-300",
    dot: "bg-green-500",
  },
  tryout: {
    bg: "bg-orange-100 dark:bg-orange-900/30",
    border: "border-orange-300 dark:border-orange-700",
    text: "text-orange-800 dark:text-orange-300",
    dot: "bg-orange-500",
  },
};

const FILTER_LABELS: Record<EventTypeFilter, string> = {
  all: "All",
  match_day: "Match Days",
  practice: "Practices",
  tryout: "Tryouts",
};

const HOURS = Array.from({ length: 15 }, (_, i) => i + 6); // 6am - 8pm

function getDateRange(date: Date, mode: ViewMode): { start: string; end: string } {
  if (mode === "month" || mode === "list") {
    const monthStart = startOfMonth(date);
    const monthEnd = endOfMonth(date);
    const calStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const calEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
    return { start: format(calStart, "yyyy-MM-dd"), end: format(calEnd, "yyyy-MM-dd") };
  }
  if (mode === "week") {
    const weekStart = startOfWeek(date, { weekStartsOn: 1 });
    const weekEnd = endOfWeek(date, { weekStartsOn: 1 });
    return { start: format(weekStart, "yyyy-MM-dd"), end: format(weekEnd, "yyyy-MM-dd") };
  }
  // day
  return { start: format(date, "yyyy-MM-dd"), end: format(date, "yyyy-MM-dd") };
}

function getMultiMonthDateRange(date: Date): { start: string; end: string } {
  const firstMonth = subMonths(date, 1);
  const lastMonth = addMonths(date, 2);
  const calStart = startOfWeek(startOfMonth(firstMonth), { weekStartsOn: 1 });
  const calEnd = endOfWeek(endOfMonth(lastMonth), { weekStartsOn: 1 });
  return { start: format(calStart, "yyyy-MM-dd"), end: format(calEnd, "yyyy-MM-dd") };
}

function navigateDate(date: Date, mode: ViewMode, direction: "prev" | "next"): Date {
  const delta = direction === "next" ? 1 : -1;
  if (mode === "month" || mode === "list") return delta > 0 ? addMonths(date, 1) : subMonths(date, 1);
  if (mode === "week") return delta > 0 ? addWeeks(date, 1) : subWeeks(date, 1);
  return delta > 0 ? addDays(date, 1) : subDays(date, 1);
}

function getTitle(date: Date, mode: ViewMode): string {
  if (mode === "month" || mode === "list") return format(date, "MMMM yyyy");
  if (mode === "week") {
    const ws = startOfWeek(date, { weekStartsOn: 1 });
    const we = endOfWeek(date, { weekStartsOn: 1 });
    if (ws.getMonth() === we.getMonth()) {
      return `${format(ws, "d")} - ${format(we, "d MMMM yyyy")}`;
    }
    return `${format(ws, "d MMM")} - ${format(we, "d MMM yyyy")}`;
  }
  return format(date, "EEEE, d MMMM yyyy");
}

// --- Event positioning for drag ---
function getEventTopAndHeight(item: { starts_at: string; ends_at: string }, dayStart: Date) {
  const itemStart = parseISO(item.starts_at);
  const itemEnd = parseISO(item.ends_at);
  const topMinutes = Math.max(0, differenceInMinutes(itemStart, setHours(setMinutes(dayStart, 0), 6)));
  const durationMinutes = Math.max(30, differenceInMinutes(itemEnd, itemStart));
  return {
    top: (topMinutes / 60) * 64, // 64px per hour
    height: Math.max(24, (durationMinutes / 60) * 64),
  };
}

export default function CalendarPage() {
  const { user: authUser } = useAuth();
  const canManage = authUser?.roles.some((r) => r.role_type !== "super_parent" && r.role_type !== "parent" && r.role_type !== "player") ?? false;
  const { schoolId } = useParams<{ schoolId: string }>();
  const nav = useNavigate();
  const isMdUp = useMediaQuery("(min-width: 768px)");

  const [mode, setMode] = useState<ViewMode>("month");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [availableSports, setAvailableSports] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [schoolName, setSchoolName] = useState("");

  // 184: dnd-kit drag state -- the event currently being dragged (shown in the
  // DragOverlay). Day-cells are droppables; the drop reschedules to that date.
  const sensors = useDndSensors();
  const [activeEvent, setActiveEvent] = useState<CalendarEvent | null>(null);

  // Quick create dialog state
  const [quickCreateDate, setQuickCreateDate] = useState<string | null>(null);
  const [quickCreateType, setQuickCreateType] = useState<string | null>(null);
  const [courts, setCourts] = useState<CourtPublic[]>([]);

  // Context menu state
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; dateStr: string } | null>(null);

  // Detail pane state
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [detailPinned, setDetailPinned] = useState(false);

  // Event context menu state
  const [eventContextMenu, setEventContextMenu] = useState<{ x: number; y: number; event: CalendarEvent } | null>(null);

  // Filter state
  const [eventTypeFilter, setEventTypeFilter] = useState<EventTypeFilter>("all");
  const [myEventsFilter, setMyEventsFilter] = useState(false);
  const [sportFilters, setSportFilters] = useState<string[]>([]);

  // Selected date (highlighted day)
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  // Track previous mode for back navigation from day drill-down
  const [previousMode, setPreviousMode] = useState<ViewMode | null>(null);

  // Multi-month mode
  const [multiMonth, setMultiMonth] = useState(false);

  // 080 -- Show cancelled events toggle (localStorage-persisted per user session)
  const [showCancelled, setShowCancelled] = useState<boolean>(() => {
    return typeof window !== "undefined" && localStorage.getItem("calendar.showCancelled") === "true";
  });
  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("calendar.showCancelled", String(showCancelled));
    }
  }, [showCancelled]);

  useEffect(() => {
    if (!schoolId) return;
    api.getSchool(schoolId).then((res) => setSchoolName(res.school.name)).catch(() => {});
    api.getCourts(schoolId).then((res) => setCourts(res.courts)).catch(() => {});
  }, [schoolId]);

  const fetchCalendar = useCallback(() => {
    if (!schoolId) return;
    setLoading(true);
    setError(null);
    const { start, end } = (mode === "month" && multiMonth)
      ? getMultiMonthDateRange(currentDate)
      : getDateRange(currentDate, mode);
    const filters: { my_events?: boolean; include_cancelled?: boolean } = {};
    if (myEventsFilter) filters.my_events = true;
    if (showCancelled) filters.include_cancelled = true;
    api
      .getCalendar(schoolId, start, end, filters)
      .then((res) => {
        setEvents(res.events);
        setAvailableSports(res.available_sports);
      })
      .catch((e) =>
        setError(e instanceof api.ApiError ? e.message : "Failed to load calendar"),
      )
      .finally(() => setLoading(false));
  }, [schoolId, currentDate, mode, multiMonth, myEventsFilter, showCancelled]);

  useEffect(() => {
    fetchCalendar();
  }, [fetchCalendar]);

  // Mobile fetches a whole-month range so the agenda/month/day views all have
  // data from one request; the mobile component drives the presentation.
  useEffect(() => {
    if (!isMdUp) {
      setMode("month");
      setMultiMonth(false);
    }
  }, [isMdUp]);

  // Close context menu on click elsewhere or Escape
  useEffect(() => {
    function handleClick() {
      setContextMenu(null);
      setEventContextMenu(null);
    }
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setContextMenu(null);
        setEventContextMenu(null);
        setSelectedEvent(null);
      }
    }
    document.addEventListener("click", handleClick);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("click", handleClick);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  // Filtered events
  const filteredEvents = useMemo(() => {
    let filtered = events;
    if (eventTypeFilter !== "all") {
      filtered = filtered.filter((e) => e.event_type === eventTypeFilter);
    }
    if (sportFilters.length > 0) {
      filtered = filtered.filter(
        (e) => e.sport_name === null || sportFilters.some(
          (s) => s.toLowerCase() === e.sport_name!.toLowerCase(),
        ),
      );
    }
    return filtered;
  }, [events, eventTypeFilter, sportFilters]);

  // Group events by date for month view
  const eventsByDate = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    for (const event of filteredEvents) {
      const dateKey = format(parseISO(event.starts_at), "yyyy-MM-dd");
      const arr = map.get(dateKey) || [];
      arr.push(event);
      map.set(dateKey, arr);
    }
    return map;
  }, [filteredEvents]);

  // --- Context menu handler ---
  function handleContextMenu(e: React.MouseEvent, dateStr: string) {
    if (!canManage) return;
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, dateStr });
  }

  function handleContextMenuSelect(actionType: string) {
    if (!contextMenu) return;
    setQuickCreateType(actionType);
    setQuickCreateDate(contextMenu.dateStr);
    setContextMenu(null);
  }

  function handleEventContextMenu(e: React.MouseEvent, event: CalendarEvent) {
    if (!canManage) return;
    e.preventDefault();
    setContextMenu(null);
    setEventContextMenu({ x: e.clientX, y: e.clientY, event });
  }

  function handleEditEvent(event: CalendarEvent) {
    nav(`/events/${event.id}`);
  }

  async function handleDeleteEvent(event: CalendarEvent) {
    if (!confirm(`Delete event "${event.name}"? This will also delete all its matches.`)) return;
    try {
      await api.deleteEvent(event.id);
      if (selectedEvent?.id === event.id) setSelectedEvent(null);
      fetchCalendar();
    } catch {
      alert("Failed to delete event");
    }
  }

  // --- 184: dnd-kit drag handlers ---
  // Reschedule an event to another day by dragging its chip onto a day-cell
  // (works on touch via the shared Touch sensor -- long-press to drag). The
  // dragged event keeps its time-of-day; only the date shifts. The day
  // droppable carries the target `dateStr` in its data.
  function handleDragStart(e: DragStartEvent) {
    if (!canManage) return;
    const eventId = e.active.data.current?.eventId as string | undefined;
    setActiveEvent(events.find((ev) => ev.id === eventId) ?? null);
  }

  async function handleDragEnd(e: DragEndEvent) {
    setActiveEvent(null);
    if (!canManage) return;
    const eventId = e.active.data.current?.eventId as string | undefined;
    const dateStr = e.over?.data.current?.dateStr as string | undefined;
    if (!eventId || !dateStr) return;

    const event = events.find((ev) => ev.id === eventId);
    if (!event) return;

    const oldStart = parseISO(event.starts_at);
    const oldEnd = parseISO(event.ends_at);
    const durationMs = oldEnd.getTime() - oldStart.getTime();
    const targetDate = parseISO(dateStr + "T00:00:00");
    const newStart = new Date(targetDate);
    newStart.setHours(oldStart.getHours(), oldStart.getMinutes(), 0, 0);
    // Dropped back on the same day -- nothing to do.
    if (newStart.getTime() === oldStart.getTime()) return;
    const newEnd = new Date(newStart.getTime() + durationMs);

    const newStartISO = newStart.toISOString();
    const newEndISO = newEnd.toISOString();
    // 127 R127 -- API propagates this delta to every child match, so the
    // optimistic local state must shift match times by the same amount or
    // the calendar will momentarily render matches at the old date.
    const deltaMs = newStart.getTime() - oldStart.getTime();

    setEvents((prev) =>
      prev.map((ev) =>
        ev.id === eventId
          ? {
              ...ev,
              starts_at: newStartISO,
              ends_at: newEndISO,
              matches: ev.matches.map((m) => ({
                ...m,
                starts_at: new Date(parseISO(m.starts_at).getTime() + deltaMs).toISOString(),
                ends_at: new Date(parseISO(m.ends_at).getTime() + deltaMs).toISOString(),
              })),
            }
          : ev,
      ),
    );

    try {
      await api.updateEvent(event.id, {
        starts_at: newStartISO,
        ends_at: newEndISO,
      });
    } catch {
      // Rollback on failure -- refetch picks up the original event AND
      // un-propagated match times.
      fetchCalendar();
    }
  }

  function handlePrev() {
    setCurrentDate((d) => navigateDate(d, mode, "prev"));
  }

  function handleNext() {
    setCurrentDate((d) => navigateDate(d, mode, "next"));
  }

  function handleToday() {
    setCurrentDate(new Date());
  }

  function openQuickCreate(dateStr: string, eventType?: string) {
    if (!canManage) return;
    setQuickCreateType(eventType ?? null);
    setQuickCreateDate(dateStr);
  }

  function drillDownToDay(date: Date) {
    setPreviousMode(mode);
    setCurrentDate(date);
    setMode("day");
  }

  function handleBackFromDay() {
    if (previousMode) {
      setMode(previousMode);
      setPreviousMode(null);
    }
  }

  // --- Month grid ---
  const monthDays = useMemo(() => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const calStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const calEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
    return eachDayOfInterval({ start: calStart, end: calEnd });
  }, [currentDate]);

  // --- Multi-month grids (1 before + current + 2 after) ---
  const multiMonthGrids = useMemo(() => {
    if (!multiMonth) return [];
    const months: { date: Date; days: Date[] }[] = [];
    for (let offset = -1; offset <= 2; offset++) {
      const m = addMonths(currentDate, offset);
      const ms = startOfMonth(m);
      const me = endOfMonth(m);
      const cs = startOfWeek(ms, { weekStartsOn: 1 });
      const ce = endOfWeek(me, { weekStartsOn: 1 });
      months.push({ date: m, days: eachDayOfInterval({ start: cs, end: ce }) });
    }
    return months;
  }, [currentDate, multiMonth]);

  // --- Week days ---
  const weekDays = useMemo(() => {
    const ws = startOfWeek(currentDate, { weekStartsOn: 1 });
    const we = endOfWeek(currentDate, { weekStartsOn: 1 });
    return eachDayOfInterval({ start: ws, end: we });
  }, [currentDate]);

  // --- Render ---
  // Mobile: dedicated agenda/day/month calendar (one tree, no drag-grid).
  if (!isMdUp) {
    return (
      <MobileCalendar
        events={filteredEvents}
        monthDate={currentDate}
        setMonthDate={setCurrentDate}
        typeFilter={eventTypeFilter}
        setTypeFilter={setEventTypeFilter}
        sportFilters={sportFilters}
        setSportFilters={setSportFilters}
        availableSports={availableSports}
        canManage={canManage}
        schoolId={schoolId}
      />
    );
  }

  return (
    <div className="flex h-full gap-0" data-page="calendar" data-state={loading ? "loading" : error ? "error" : "ready"}>
    <div className="min-w-0 flex-1 flex flex-col gap-4">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-region="toolbar">
        <div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Link to={`/schools/${schoolId}`} className="hover:underline">
              {schoolName || "School"}
            </Link>
            <span>/</span>
            <span>Calendar</span>
          </div>
          <h1 className="text-2xl font-semibold">Calendar</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setDetailPinned((p) => !p)} title={detailPinned ? "Close preview panel" : "Open preview panel"}>
            {detailPinned ? <PanelRightClose className="h-4 w-4" /> : <PanelRightOpen className="h-4 w-4" />}
          </Button>
          {canManage && (
            <Button size="sm" onClick={() => openQuickCreate(selectedDate ?? format(currentDate, "yyyy-MM-dd"))}>
              <Plus className="mr-2 h-4 w-4" />
              New Event
            </Button>
          )}
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          {previousMode && mode === "day" && (
            <Button variant="outline" size="sm" onClick={handleBackFromDay}>
              <ChevronLeft className="mr-1 h-4 w-4" />
              Back to {previousMode.charAt(0).toUpperCase() + previousMode.slice(1)}
            </Button>
          )}
          {!(mode === "month" && multiMonth) && (
            <>
              <Button variant="outline" size="sm" onClick={handlePrev}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={handleToday}>
                Today
              </Button>
              <Button variant="outline" size="sm" onClick={handleNext}>
                <ChevronRight className="h-4 w-4" />
              </Button>
              <span className="ml-2 text-lg font-medium">
                {getTitle(currentDate, mode)}
              </span>
            </>
          )}
          {mode === "month" && multiMonth && (
            <span className="text-lg font-medium">
              {format(subMonths(currentDate, 1), "MMM yyyy")} - {format(addMonths(currentDate, 2), "MMM yyyy")}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {mode === "month" && (
            <Button
              variant={multiMonth ? "default" : "outline"}
              size="sm"
              onClick={() => setMultiMonth((v) => !v)}
              title="Toggle multi-month view"
            >
              <Layers className="mr-1 h-4 w-4" />
              Multi
            </Button>
          )}
          <div className="flex gap-1 rounded-md border border-input p-0.5">
            {(["month", "week", "day", "list"] as ViewMode[]).map((m) => (
              <button
                key={m}
                onClick={() => {
                  setMode(m);
                  setPreviousMode(null);
                  if (m !== "month") setMultiMonth(false);
                }}
                className={`flex items-center gap-1 rounded px-3 py-1 text-sm font-medium transition-colors ${
                  mode === m
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {m === "list" && <List className="h-3.5 w-3.5" />}
                {m.charAt(0).toUpperCase() + m.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Filter Chips */}
      <div className="flex flex-wrap gap-2">
        {/* My Events filter */}
        <button
          onClick={() => setMyEventsFilter((v) => !v)}
          className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm font-medium transition-colors ${
            myEventsFilter
              ? "border-amber-400 bg-amber-100 text-amber-800 dark:border-amber-600 dark:bg-amber-900/30 dark:text-amber-300"
              : "border-border text-muted-foreground hover:border-foreground/30 hover:text-foreground"
          }`}
        >
          <Star className={`h-3 w-3 ${myEventsFilter ? "fill-amber-500" : ""}`} />
          My Events
        </button>

        {/* 080 -- Show cancelled events toggle */}
        <button
          onClick={() => setShowCancelled((v) => !v)}
          data-action="toggle-show-cancelled"
          className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm font-medium transition-colors ${
            showCancelled
              ? "border-red-400 bg-red-50 text-red-800 dark:border-red-700 dark:bg-red-950/30 dark:text-red-200"
              : "border-border text-muted-foreground hover:border-foreground/30 hover:text-foreground"
          }`}
          title="Include cancelled events in the calendar"
        >
          Show cancelled
        </button>

        {/* Divider */}
        <span className="mx-1 self-center text-border">|</span>

        {/* Event type filters */}
        {(Object.keys(FILTER_LABELS) as EventTypeFilter[]).map((key) => {
          const isActive = eventTypeFilter === key;
          const colors = key !== "all" ? EVENT_COLORS[key] : null;
          return (
            <button
              key={key}
              onClick={() => setEventTypeFilter(key)}
              className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm font-medium transition-colors ${
                isActive
                  ? "border-primary bg-primary/10 text-primary"
                  : "border-border text-muted-foreground hover:border-foreground/30 hover:text-foreground"
              }`}
            >
              {colors && <span className={`inline-block h-2 w-2 rounded-full ${colors.dot}`} />}
              {FILTER_LABELS[key]}
            </button>
          );
        })}

        {/* Sport filters */}
        {availableSports.length > 0 && (
          <>
            <span className="mx-1 self-center text-border">|</span>
            {/* All sports chip */}
            <button
              onClick={() => setSportFilters([])}
              className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm font-medium transition-colors ${
                sportFilters.length === 0
                  ? "border-primary bg-primary/10 text-primary"
                  : "border-border text-muted-foreground hover:border-foreground/30 hover:text-foreground"
              }`}
            >
              All Sports
            </button>
            {availableSports.map((sport) => {
              const isActive = sportFilters.includes(sport);
              return (
                <button
                  key={sport}
                  onClick={(e) => {
                    if (e.shiftKey) {
                      // Shift+click: toggle this sport in multi-select
                      setSportFilters((prev) =>
                        prev.includes(sport)
                          ? prev.filter((s) => s !== sport)
                          : [...prev, sport],
                      );
                    } else {
                      // Plain click: select only this sport, or clear if already the sole selection
                      setSportFilters((prev) =>
                        prev.length === 1 && prev[0] === sport ? [] : [sport],
                      );
                    }
                  }}
                  className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm font-medium transition-colors ${
                    isActive
                      ? "border-indigo-400 bg-indigo-100 text-indigo-800 dark:border-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-300"
                      : "border-border text-muted-foreground hover:border-foreground/30 hover:text-foreground"
                  }`}
                  title="Shift+click to multi-select"
                >
                  <span className="inline-block h-2 w-2 rounded-full bg-indigo-500" />
                  {sport}
                </button>
              );
            })}
          </>
        )}
      </div>

      {error && <p className="text-sm text-destructive" data-error>{error}</p>}
      {loading && <p className="text-muted-foreground">Loading...</p>}

      <DndContext
        sensors={sensors}
        collisionDetection={pointerWithin}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        {!loading && !error && mode === "month" && !multiMonth && (
          <MonthView
            days={monthDays}
            currentDate={currentDate}
            selectedDate={selectedDate}
            eventsByDate={eventsByDate}
            onDayClick={drillDownToDay}
            onSelectDay={setSelectedDate}
            onAddEvent={(dateStr) => openQuickCreate(dateStr)}
            onContextMenu={handleContextMenu}
            onSelectEvent={setSelectedEvent}
            onEditEvent={handleEditEvent}
            onEventContextMenu={handleEventContextMenu}
          />
        )}

        {!loading && !error && mode === "month" && multiMonth && (
          <MultiMonthView
            months={multiMonthGrids}
            selectedDate={selectedDate}
            eventsByDate={eventsByDate}
            onDayClick={drillDownToDay}
            onSelectDay={setSelectedDate}
            onAddEvent={(dateStr) => openQuickCreate(dateStr)}
            onContextMenu={handleContextMenu}
            onSelectEvent={setSelectedEvent}
            onEditEvent={handleEditEvent}
            onEventContextMenu={handleEventContextMenu}
          />
        )}

        {!loading && !error && mode === "week" && (
          <WeekView
            days={weekDays}
            events={filteredEvents}
            onDayClick={drillDownToDay}
            onAddEvent={(dateStr) => openQuickCreate(dateStr)}
            onContextMenu={handleContextMenu}
            onSelectEvent={setSelectedEvent}
            onEditEvent={handleEditEvent}
            onEventContextMenu={handleEventContextMenu}
          />
        )}

        {!loading && !error && mode === "day" && (
          <DayView
            date={currentDate}
            events={filteredEvents.filter((e) =>
              isSameDay(parseISO(e.starts_at), currentDate),
            )}
            onAddEvent={(dateStr) => openQuickCreate(dateStr)}
            onContextMenu={handleContextMenu}
            onSelectEvent={setSelectedEvent}
            onEditEvent={handleEditEvent}
            onEventContextMenu={handleEventContextMenu}
          />
        )}

        {/* 184: dragged event preview, rendered in a portal so it is never
            clipped by the week/day scroll containers' overflow. */}
        <DragOverlay>
          {activeEvent ? (
            <div className="pointer-events-none rounded border border-primary bg-background px-2 py-1 text-[11px] font-medium shadow-lg">
              {format(parseISO(activeEvent.starts_at), "HH:mm")} {activeEvent.name}
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      {!loading && !error && mode === "list" && (
        <ListView
          events={filteredEvents}
          selectedEvent={selectedEvent}
          onSelectEvent={setSelectedEvent}
        />
      )}

      {/* Context Menu */}
      {canManage && contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          onSelect={handleContextMenuSelect}
        />
      )}

      {canManage && eventContextMenu && (
        <EventContextMenu
          x={eventContextMenu.x}
          y={eventContextMenu.y}
          event={eventContextMenu.event}
          onEdit={() => {
            handleEditEvent(eventContextMenu.event);
            setEventContextMenu(null);
          }}
          onDelete={() => {
            handleDeleteEvent(eventContextMenu.event);
            setEventContextMenu(null);
          }}
        />
      )}

      {canManage && quickCreateDate && schoolId && (
        <QuickCreateEventDialog
          schoolId={schoolId}
          date={quickCreateDate}
          initialEventType={quickCreateType}
          onClose={() => {
            setQuickCreateDate(null);
            setQuickCreateType(null);
          }}
          onCreated={(eventId) => {
            setQuickCreateDate(null);
            setQuickCreateType(null);
            nav(`/events/${eventId}`);
          }}
        />
      )}
    </div>
    {/* Dockable Detail Panel */}
    {detailPinned && (
    <div
      className="w-[360px] flex-shrink-0 border-l border-border bg-background overflow-hidden"
      data-region="detail-pane"
    >
      <div className="flex h-full w-[360px] flex-col">
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <h2 className="text-base font-semibold">Event Details</h2>
          <Button variant="ghost" size="sm" onClick={() => setDetailPinned(false)} title="Close detail panel">
            <PanelRightClose className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-4">
          {selectedEvent && <EventDetailContent event={selectedEvent} canManage={canManage} onDeleted={() => { setSelectedEvent(null); fetchCalendar(); }} />}
          {!selectedEvent && (
            <p className="text-sm text-muted-foreground">Click an event to preview details</p>
          )}
        </div>
      </div>
    </div>
    )}
    </div>
  );
}

// --- Detail Content Components ---

function EventDetailContent({ event, canManage, onDeleted }: { event: CalendarEvent; canManage: boolean; onDeleted: () => void }) {
  const colors = EVENT_COLORS[event.event_type] ?? EVENT_COLORS.match_day;
  const typeLabel: Record<string, string> = {
    match_day: "Match Day",
    practice: "Practice",
    tryout: "Tryout",
  };

  return (
    <>
      {/* Type badge */}
      <div className="flex flex-wrap items-center gap-2">
        <span
          className={`inline-flex w-fit items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium ${colors.bg} ${colors.text} ${colors.border}`}
        >
          <span className={`inline-block h-2 w-2 rounded-full ${colors.dot}`} />
          {typeLabel[event.event_type] ?? event.event_type}
        </span>
        <EventStatusBadge event={event} />
      </div>

      <h3 className="text-lg font-semibold">{event.name}</h3>

      <div className="flex flex-col gap-1 text-sm">
        <div className="text-muted-foreground">Date & Time</div>
        <div>{format(parseISO(event.starts_at), "EEEE, d MMMM yyyy")}</div>
        <div>{format(parseISO(event.starts_at), "HH:mm")} - {format(parseISO(event.ends_at), "HH:mm")}</div>
      </div>

      {event.courts?.length > 0 && (
        <div className="flex flex-col gap-1 text-sm">
          <div className="text-muted-foreground">Court</div>
          <div>{[...event.courts].sort((a, b) => a.name.localeCompare(b.name)).map(c => c.name).join(", ")}</div>
        </div>
      )}

      {event.sport_name && (
        <div className="flex flex-col gap-1 text-sm">
          <div className="text-muted-foreground">Sport</div>
          <div>{event.sport_name}</div>
        </div>
      )}

      {event.schools && event.schools.length > 0 && (
        <div className="flex flex-col gap-1 text-sm">
          <div className="text-muted-foreground">Schools ({event.schools.length})</div>
          <div className="flex flex-wrap gap-1">
            {event.schools.map((s) => (
              <span
                key={s.id}
                className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs ${
                  s.school_id === event.school_id
                    ? "bg-primary/10 font-medium text-primary"
                    : "bg-muted text-muted-foreground"
                }`}
              >
                <SchoolCrest name={s.name} logoUrl={s.logo_url} primaryColor={s.primary_color} size={16} />
                {s.name}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-col gap-1 text-sm">
        <div className="text-muted-foreground">Matches ({event.match_count})</div>
        {event.matches.length > 0 ? (
          <div className="flex flex-col gap-2">
            {event.matches.map((m) => (
              <div key={m.id} className="rounded border border-border bg-muted/50 px-3 py-2">
                <div className="font-medium">{m.name}</div>
                <div className="text-xs text-muted-foreground">
                  {format(parseISO(m.starts_at), "HH:mm")} - {format(parseISO(m.ends_at), "HH:mm")}
                  {m.age_group && <span className="ml-2">({m.age_group})</span>}
                </div>
                {m.teams.length > 0 && (
                  <div className="mt-1 text-xs">
                    {m.teams.map((t, i) => (
                      <span key={i}>
                        {i > 0 && " vs "}
                        <span className="font-medium">{t.name}</span>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-muted-foreground">No matches</div>
        )}
      </div>

      <div className="mt-2 flex flex-col gap-2">
        <Link
          to={`/events/${event.id}`}
          className={buttonVariants({ variant: "default", size: "sm" })}
        >
          <ExternalLink className="mr-2 h-4 w-4" />
          View Full Details
        </Link>
        {canManage && (
          <>
            <Link
              to={`/events/${event.id}/edit`}
              className={buttonVariants({ variant: "outline", size: "sm" })}
            >
              <Pencil className="mr-2 h-4 w-4" />
              Edit
            </Link>
            <Button
              variant="outline"
              size="sm"
              className="text-destructive hover:bg-destructive/10"
              onClick={async () => {
                if (!confirm(`Delete event "${event.name}"? This will also delete all its matches.`)) return;
                try {
                  await api.deleteEvent(event.id);
                  onDeleted();
                } catch {
                  alert("Failed to delete event");
                }
              }}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </Button>
          </>
        )}
      </div>
    </>
  );
}

// --- Context Menu ---

function ContextMenu({
  x,
  y,
  onSelect,
}: {
  x: number;
  y: number;
  onSelect: (eventType: string) => void;
}) {
  const menuRef = useRef<HTMLDivElement>(null);

  // Adjust position if menu goes off-screen
  const [pos, setPos] = useState({ x, y });
  useEffect(() => {
    if (!menuRef.current) return;
    const rect = menuRef.current.getBoundingClientRect();
    const nx = x + rect.width > window.innerWidth ? x - rect.width : x;
    const ny = y + rect.height > window.innerHeight ? y - rect.height : y;
    setPos({ x: Math.max(0, nx), y: Math.max(0, ny) });
  }, [x, y]);

  const items = [
    { type: "match_day", label: "Add Match Day", colors: EVENT_COLORS.match_day },
    { type: "practice", label: "Add Practice", colors: EVENT_COLORS.practice },
    { type: "tryout", label: "Add Tryout", colors: EVENT_COLORS.tryout },
  ];

  return (
    <div
      ref={menuRef}
      className="fixed z-50 min-w-[200px] rounded-md border border-border bg-background p-1 shadow-xl"
      style={{ left: pos.x, top: pos.y }}
      onClick={(e) => e.stopPropagation()}
    >
      {items.map((item) =>
        item.type === "divider" ? (
          <div key="divider" className="my-1 border-t border-border" />
        ) : (
          <button
            key={item.type}
            onClick={() => onSelect(item.type)}
            className="flex w-full items-center gap-2 rounded px-3 py-2 text-sm transition-colors hover:bg-muted"
          >
            <span className={`inline-block h-2.5 w-2.5 rounded-full ${item.colors.dot}`} />
            {item.label}
          </button>
        ),
      )}
    </div>
  );
}

// --- Event Context Menu ---

function EventContextMenu({
  x,
  y,
  event,
  onEdit,
  onDelete,
}: {
  x: number;
  y: number;
  event: CalendarEvent;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ x, y });
  useEffect(() => {
    if (!menuRef.current) return;
    const rect = menuRef.current.getBoundingClientRect();
    const nx = x + rect.width > window.innerWidth ? x - rect.width : x;
    const ny = y + rect.height > window.innerHeight ? y - rect.height : y;
    setPos({ x: Math.max(0, nx), y: Math.max(0, ny) });
  }, [x, y]);

  const colors = EVENT_COLORS[event.event_type] ?? EVENT_COLORS.match_day;

  return (
    <div
      ref={menuRef}
      className="fixed z-50 min-w-[200px] rounded-md border border-border bg-background p-1 shadow-xl"
      style={{ left: pos.x, top: pos.y }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="px-3 py-1.5 text-xs font-medium text-muted-foreground truncate max-w-[200px] flex items-center gap-1.5">
        <span className={`inline-block h-2 w-2 rounded-full ${colors.dot}`} />
        {event.name}
      </div>
      <div className="my-1 border-t border-border" />
      <button
        onClick={onEdit}
        className="flex w-full items-center gap-2 rounded px-3 py-2 text-sm transition-colors hover:bg-muted"
      >
        <Pencil className="h-3.5 w-3.5" />
        Edit Event
      </button>
      <button
        onClick={onDelete}
        className="flex w-full items-center gap-2 rounded px-3 py-2 text-sm text-destructive transition-colors hover:bg-destructive/10"
      >
        <Trash2 className="h-3.5 w-3.5" />
        Delete Event
      </button>
    </div>
  );
}

// --- 184: dnd-kit calendar drag helpers ---

// A calendar day as a drop target. `dropId` must be unique per rendered cell
// (week/day views render many cells for the same date), while `dateStr` -- the
// date the drop reschedules to -- is carried in the droppable's data. Any extra
// props (onClick/onContextMenu/etc.) pass straight through to the div.
function DroppableDay({
  dropId,
  dateStr,
  className,
  overClassName = "bg-primary/10",
  children,
  ...rest
}: {
  dropId: string;
  dateStr: string;
  className: string;
  overClassName?: string;
  children?: React.ReactNode;
} & React.HTMLAttributes<HTMLDivElement>) {
  const { setNodeRef, isOver } = useDroppable({ id: dropId, data: { dateStr } });
  return (
    <div ref={setNodeRef} className={`${className} ${isOver ? overClassName : ""}`} {...rest}>
      {children}
    </div>
  );
}

// A draggable event chip. Works on touch via the shared Touch sensor (long-press
// to drag; a quick swipe still scrolls). A plain tap still fires onClick
// (select); double-click edits. Movement is shown by the page-level DragOverlay,
// so the source node only dims while dragging.
function DraggableEventChip({
  eventId,
  className,
  draggingClassName = "opacity-50 ring-2 ring-primary",
  style,
  children,
  onClick,
  onDoubleClick,
  onContextMenu,
}: {
  eventId: string;
  className: string;
  draggingClassName?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
  onClick?: (e: React.MouseEvent) => void;
  onDoubleClick?: (e: React.MouseEvent) => void;
  onContextMenu?: (e: React.MouseEvent) => void;
}) {
  const { setNodeRef, listeners, attributes, isDragging } = useDraggable({
    id: `event-${eventId}`,
    data: { eventId },
  });
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      data-event-chip
      style={style}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      onContextMenu={onContextMenu}
      className={`${className} ${isDragging ? draggingClassName : ""}`}
    >
      {children}
    </div>
  );
}

// --- Month View ---

function MonthView({
  days,
  currentDate,
  selectedDate,
  eventsByDate,
  onDayClick,
  onSelectDay,
  onAddEvent,
  onContextMenu,
  onSelectEvent,
  onEditEvent,
  onEventContextMenu,
}: {
  days: Date[];
  currentDate: Date;
  selectedDate: string | null;
  eventsByDate: Map<string, CalendarEvent[]>;
  onDayClick: (date: Date) => void;
  onSelectDay: (dateStr: string) => void;
  onAddEvent: (dateStr: string) => void;
  onContextMenu: (e: React.MouseEvent, dateStr: string) => void;
  onSelectEvent: (event: CalendarEvent) => void;
  onEditEvent: (event: CalendarEvent) => void;
  onEventContextMenu: (e: React.MouseEvent, event: CalendarEvent) => void;
}) {
  const weekDayHeaders = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  return (
    <div className="rounded-lg border border-border" data-region="calendar-grid">
      {/* Day headers */}
      <div className="grid grid-cols-7 border-b border-border">
        {weekDayHeaders.map((d) => (
          <div
            key={d}
            className="px-2 py-2 text-center text-xs font-medium text-muted-foreground"
          >
            {d}
          </div>
        ))}
      </div>

      {/* Day cells */}
      <div className="grid grid-cols-7">
        {days.map((day) => {
          const dateStr = format(day, "yyyy-MM-dd");
          const dayEvents = eventsByDate.get(dateStr) || [];
          const totalItems = dayEvents.length;
          const isCurrentMonth = isSameMonth(day, currentDate);
          const isSelected = selectedDate === dateStr;

          return (
            <DroppableDay
              key={dateStr}
              dropId={`month-day-${dateStr}`}
              dateStr={dateStr}
              className={`group min-h-[100px] border-b border-r border-border p-1 transition-colors ${
                !isCurrentMonth ? "bg-muted/30" : ""
              } ${isSelected ? "bg-primary/5 ring-1 ring-inset ring-primary/30" : ""}`}
              onContextMenu={(e) => onContextMenu(e, dateStr)}
              onClick={(e) => {
                if ((e.target as HTMLElement).closest("[data-event-chip], button")) return;
                onSelectDay(dateStr);
              }}
              onDoubleClick={(e) => {
                if ((e.target as HTMLElement).closest("[data-event-chip]")) return;
                onDayClick(day);
              }}
            >
              <div className="mb-1 flex items-center justify-between">
                <span
                  className={`flex h-6 w-6 items-center justify-center rounded-full text-xs ${
                    isToday(day)
                      ? "bg-primary text-primary-foreground font-bold"
                      : isCurrentMonth
                        ? "text-foreground hover:bg-muted"
                        : "text-muted-foreground"
                  }`}
                >
                  {format(day, "d")}
                </span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onAddEvent(dateStr);
                  }}
                  className="flex h-5 w-5 items-center justify-center rounded text-muted-foreground opacity-0 transition-opacity hover:bg-muted hover:text-foreground group-hover:opacity-100"
                  title="Add event"
                >
                  <Plus className="h-3 w-3" />
                </button>
              </div>

              {/* Event blocks */}
              {dayEvents.slice(0, 3).map((event) => {
                const colors = EVENT_COLORS[event.event_type] ?? EVENT_COLORS.match_day;
                return (
                  <DraggableEventChip
                    key={event.id}
                    eventId={event.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelectEvent(event);
                    }}
                    onDoubleClick={(e) => {
                      e.stopPropagation();
                      onEditEvent(event);
                    }}
                    onContextMenu={(e) => {
                      e.stopPropagation();
                      onEventContextMenu(e, event);
                    }}
                    className={`mb-0.5 block truncate rounded px-1 text-[10px] leading-4 ${colors.bg} ${colors.text} border ${colors.border} hover:opacity-80 cursor-grab active:cursor-grabbing`}
                  >
                    {format(parseISO(event.starts_at), "HH:mm")} {event.name}
                  </DraggableEventChip>
                );
              })}

              {totalItems > 3 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDayClick(day);
                  }}
                  className="text-[10px] text-muted-foreground hover:underline"
                >
                  +{totalItems - 3} more
                </button>
              )}
            </DroppableDay>
          );
        })}
      </div>
    </div>
  );
}

// --- Multi-Month View ---

function MultiMonthView({
  months,
  selectedDate,
  eventsByDate,
  onDayClick,
  onSelectDay,
  onAddEvent,
  onContextMenu,
  onSelectEvent,
  onEditEvent,
  onEventContextMenu,
}: {
  months: { date: Date; days: Date[] }[];
  selectedDate: string | null;
  eventsByDate: Map<string, CalendarEvent[]>;
  onDayClick: (date: Date) => void;
  onSelectDay: (dateStr: string) => void;
  onAddEvent: (dateStr: string) => void;
  onContextMenu: (e: React.MouseEvent, dateStr: string) => void;
  onSelectEvent: (event: CalendarEvent) => void;
  onEditEvent: (event: CalendarEvent) => void;
  onEventContextMenu: (e: React.MouseEvent, event: CalendarEvent) => void;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);

  return (
    <div ref={scrollRef} className="max-h-[80vh] overflow-y-auto space-y-6" data-region="calendar-grid">
      {months.map((m) => (
        <div key={format(m.date, "yyyy-MM")} className="space-y-2">
          <h3 className="sticky top-0 z-10 bg-background py-1 text-lg font-semibold">
            {format(m.date, "MMMM yyyy")}
          </h3>
          <MonthView
            days={m.days}
            currentDate={m.date}
            selectedDate={selectedDate}
            eventsByDate={eventsByDate}
            onDayClick={onDayClick}
            onSelectDay={onSelectDay}
            onAddEvent={onAddEvent}
            onContextMenu={onContextMenu}
            onSelectEvent={onSelectEvent}
            onEditEvent={onEditEvent}
            onEventContextMenu={onEventContextMenu}
          />
        </div>
      ))}
    </div>
  );
}

// --- Week View ---

function WeekView({
  days,
  events,
  onDayClick,
  onAddEvent,
  onContextMenu,
  onSelectEvent,
  onEditEvent,
  onEventContextMenu,
}: {
  days: Date[];
  events: CalendarEvent[];
  onDayClick: (date: Date) => void;
  onAddEvent: (dateStr: string) => void;
  onContextMenu: (e: React.MouseEvent, dateStr: string) => void;
  onSelectEvent: (event: CalendarEvent) => void;
  onEditEvent: (event: CalendarEvent) => void;
  onEventContextMenu: (e: React.MouseEvent, event: CalendarEvent) => void;
}) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border" data-region="calendar-grid">
      <div className="min-w-[700px]">
        {/* Day headers */}
        <div className="grid grid-cols-[60px_repeat(7,1fr)_60px] border-b border-border">
          <div className="border-r border-border" />
          {days.map((day) => (
            <div
              key={day.toISOString()}
              className={`px-2 py-2 text-center text-sm cursor-pointer ${
                isToday(day) ? "font-bold text-primary" : "text-muted-foreground"
              } hover:bg-muted/50`}
              onDoubleClick={() => onDayClick(day)}
            >
              <div className="text-xs">{format(day, "EEE")}</div>
              <div>{format(day, "d")}</div>
            </div>
          ))}
          <div className="border-l border-border" />
        </div>

        {/* Hour grid */}
        <div className="relative">
          {HOURS.map((hour) => (
            <div
              key={hour}
              className="grid grid-cols-[60px_repeat(7,1fr)_60px] border-b border-border"
              style={{ height: 64 }}
            >
              <div className="border-r border-border px-1 py-0.5 text-right text-xs text-muted-foreground">
                {String(hour).padStart(2, "0")}:00
              </div>
              {days.map((day) => {
                const dateStr = format(day, "yyyy-MM-dd");
                return (
                  <DroppableDay
                    key={dateStr + hour}
                    dropId={`week-cell-${dateStr}-${hour}`}
                    dateStr={dateStr}
                    className="relative border-r border-border cursor-pointer hover:bg-muted/50"
                    onClick={() => onAddEvent(dateStr)}
                    onDoubleClick={() => onDayClick(day)}
                    onContextMenu={(e) => onContextMenu(e, dateStr)}
                  />
                );
              })}
              <div className="border-l border-border px-1 py-0.5 text-left text-xs text-muted-foreground">
                {String(hour).padStart(2, "0")}:00
              </div>
            </div>
          ))}

          {/* Event overlays */}
          {days.map((day, dayIdx) => {
            const dayEvents = events.filter((e) =>
              isSameDay(parseISO(e.starts_at), day),
            );
            return dayEvents.map((event) => {
              const { top, height } = getEventTopAndHeight(event, day);
              const colors = EVENT_COLORS[event.event_type] ?? EVENT_COLORS.match_day;
              return (
                <DraggableEventChip
                  key={event.id}
                  eventId={event.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelectEvent(event);
                  }}
                  onDoubleClick={(e) => {
                    e.stopPropagation();
                    onEditEvent(event);
                  }}
                  onContextMenu={(e) => {
                    e.stopPropagation();
                    onEventContextMenu(e, event);
                  }}
                  className={`absolute z-10 flex flex-col cursor-grab hover:opacity-90 active:cursor-grabbing rounded border overflow-hidden ${colors.border} ${event.status === "draft" ? "border-dashed" : ""}`}
                  style={{
                    top: `${top - (event.courts?.length > 1 ? 46 : 34)}px`,
                    height: `${height + (event.courts?.length > 1 ? 46 : 34)}px`,
                    left: `calc(60px + ${dayIdx} * ((100% - 120px) / 7) + 2px)`,
                    width: `calc((100% - 120px) / 7 - 4px)`,
                  }}
                >
                  {/* Event header -- fixed height, slightly darker */}
                  <div className={`flex-shrink-0 border-b px-1 py-0.5 text-[10px] leading-tight ${colors.border} ${colors.text}`} style={{ background: "rgba(0,0,0,0.06)" }}>
                    <div className="font-semibold truncate">{event.name}</div>
                    <div className="truncate opacity-70">
                      {format(parseISO(event.starts_at), "HH:mm")}-{format(parseISO(event.ends_at), "HH:mm")}
                    </div>
                    {event.courts?.length > 1 && (
                      <div className="flex border-t border-current/10 mt-0.5 pt-0.5">
                        {[...event.courts].sort((a, b) => a.name.localeCompare(b.name)).map((c, ci) => (
                          <div key={c.id} className={`flex-1 truncate text-center text-[8px] font-semibold opacity-50 ${ci > 0 ? "border-l border-current/10" : ""}`}>
                            {c.name}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {/* Event body -- split into court columns with matches at time offsets */}
                  {(() => {
                    const evStart = parseISO(event.starts_at).getTime();
                    const evEnd = parseISO(event.ends_at).getTime();
                    const evDurationMin = Math.max(30, (evEnd - evStart) / 60_000);
                    const courtNames = event.courts?.length > 0
                      ? [...event.courts].sort((a, b) => a.name.localeCompare(b.name)).map((c) => c.name)
                      : [null]; // single column if no courts
                    return (
                      <div className={`flex flex-1 overflow-hidden ${colors.bg} ${colors.text}`}>
                        {courtNames.map((courtName, ci) => {
                          const courtMatches = event.matches.filter((m) =>
                            courtName === null ? true : m.court_name === courtName,
                          );
                          return (
                            <div key={courtName ?? "__all__"} className={`relative flex-1 ${ci > 0 ? "border-l border-current/10" : ""}`}>
                              {/* Matches */}
                              {courtMatches.map((m) => {
                                const mStart = parseISO(m.starts_at).getTime();
                                const offsetMin = Math.max(0, (mStart - evStart) / 60_000);
                                const topPx = (offsetMin / evDurationMin) * height;
                                return (
                                  <div
                                    key={m.id}
                                    className="absolute left-0 right-0 truncate border-t border-current/10 px-0.5 text-center text-[9px] opacity-70"
                                    style={{ top: topPx }}
                                  >
                                    {m.teams.length >= 2
                                      ? `${m.teams[0].name} vs ${m.teams[1].name}`
                                      : m.name}
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </DraggableEventChip>
              );
            });
          })}

        </div>
      </div>
    </div>
  );
}

// --- Quick Create Event Dialog ---

function QuickCreateEventDialog({
  schoolId,
  date,
  initialEventType,
  onClose,
  onCreated,
}: {
  schoolId: string;
  date: string;
  initialEventType: string | null;
  onClose: () => void;
  onCreated: (eventId: string) => void;
}) {

  const [name, setName] = useState("");
  const [eventDate, setEventDate] = useState(date);
  const [eventType, setEventType] = useState(initialEventType ?? "match_day");
  const [sportId, setSportId] = useState("");
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("17:00");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // School sports for sport dropdown
  const [schoolSports, setSchoolSports] = useState<{ sport_id: string; sport_name: string; court_type_name: string | null }[]>([]);
  // School search results (debounced API search)
  const [schoolSearchResults, setSchoolSearchResults] = useState<{ id: string; name: string }[]>([]);
  const [schoolSearchLoading, setSchoolSearchLoading] = useState(false);
  // Added schools: { school_id, name }
  const [addedSchools, setAddedSchools] = useState<{ school_id: string; name: string }[]>([]);
  // Host school tracking (index into addedSchools, defaults to creating school once added)
  const [hostIndex, setHostIndex] = useState<number>(0);
  // Search input for school picker
  const [schoolSearch, setSchoolSearch] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  // Courts: search-and-add (same pattern as schools)
  const [hostSchoolCourts, setHostSchoolCourts] = useState<{ id: string; name: string }[]>([]);
  const [addedCourts, setAddedCourts] = useState<{ court_id: string; name: string }[]>([]);
  const [courtSearch, setCourtSearch] = useState("");
  const [showCourtDropdown, setShowCourtDropdown] = useState(false);
  const courtSearchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    api.getSchoolSports(schoolId).then((res) => setSchoolSports(res.sports)).catch(() => {});
    api.getSchool(schoolId).then((res) => {
      // Auto-add current school as host
      setAddedSchools([{ school_id: schoolId, name: res.school.name }]);
      setHostIndex(0);
    }).catch(() => {});
  }, [schoolId]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) setShowDropdown(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Close court dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (courtSearchRef.current && !courtSearchRef.current.contains(e.target as Node)) setShowCourtDropdown(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Load courts from host school when host changes
  const hostSchool = addedSchools[hostIndex];
  useEffect(() => {
    if (hostSchool?.school_id) {
      api.getCourts(hostSchool.school_id).then((res) => setHostSchoolCourts(res.courts.map((c) => ({ id: c.id, name: c.name })))).catch(() => setHostSchoolCourts([]));
    } else {
      setHostSchoolCourts([]);
    }
  }, [hostSchool?.school_id]);

  const filteredCourts = useMemo(() => {
    if (!courtSearch.trim()) return [];
    const q = courtSearch.toLowerCase();
    const addedIds = new Set(addedCourts.map((c) => c.court_id));
    return hostSchoolCourts.filter((c) => !addedIds.has(c.id) && c.name.toLowerCase().includes(q));
  }, [courtSearch, hostSchoolCourts, addedCourts]);

  function addDugadsCourt(c: { id: string; name: string }) {
    if (addedCourts.some((a) => a.court_id === c.id)) return;
    setAddedCourts((prev) => [...prev, { court_id: c.id, name: c.name }]);
    setCourtSearch("");
    setShowCourtDropdown(false);
  }

  function removeAddedCourt(name: string) {
    setAddedCourts((prev) => prev.filter((c) => c.name !== name));
  }

  // Debounced school search
  useEffect(() => {
    const q = schoolSearch.trim();
    if (q.length < 2) { setSchoolSearchResults([]); return; }
    setSchoolSearchLoading(true);
    const timer = setTimeout(() => {
      const addedIds = new Set(addedSchools.map((s) => s.school_id));
      api.searchSchools(q, { limit: 15 })
        .then((res) => setSchoolSearchResults(res.schools.filter((s: { id: string }) => !addedIds.has(s.id)).map((s: { id: string; name: string }) => ({ id: s.id, name: s.name }))))
        .catch(() => setSchoolSearchResults([]))
        .finally(() => setSchoolSearchLoading(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [schoolSearch, addedSchools]);

  function addDugadsSchool(s: { id: string; name: string }) {
    if (addedSchools.some((a) => a.school_id === s.id)) return;
    setAddedSchools((prev) => [...prev, { school_id: s.id, name: s.name }]);
    setSchoolSearch("");
    setShowDropdown(false);
  }

  function removeAddedSchool(name: string) {
    setAddedSchools((prev) => prev.filter((s) => s.name !== name));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const res = await api.createEvent(schoolId, {
        sport_id: sportId || null,
        name,
        event_type: eventType,
        starts_at: new Date(`${eventDate}T${startTime}:00`).toISOString(),
        ends_at: new Date(`${eventDate}T${endTime}:00`).toISOString(),
      });
      const eventId = res.event.id;

      // Add participating schools in parallel (with host flag)
      const schoolPromises = addedSchools.map((s, i) =>
        api.addEventSchool(eventId, { school_id: s.school_id, is_host: i === hostIndex }),
      );
      // Add courts in parallel
      const courtPromises = addedCourts.map((c) =>
        api.addEventCourt(eventId, { court_id: c.court_id }),
      );
      await Promise.all([...schoolPromises, ...courtPromises]);

      setSaving(false);
      onCreated(eventId);
    } catch (err) {
      setError(err instanceof api.ApiError ? err.message : "Failed to create event");
      setSaving(false);
    }
  }

  const courtLabel = sportId
    ? (schoolSports.find((s) => s.sport_id === sportId)?.court_type_name ?? "Court")
    : "Court";

  const selectClass = "flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

  // --- Event form ---
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 overflow-y-auto" onClick={onClose}>
      <Card className="my-8 w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base">
              <Plus className="h-4 w-4" />
              New Event
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            {error && <p className="text-sm text-destructive">{error}</p>}

            <div className="flex flex-col gap-2">
              <Label htmlFor="qc-name">Event Name</Label>
              <Input
                id="qc-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Menlopark vs Affies Rugby Day"
                required
                autoFocus
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="qc-date">Date</Label>
              <Input
                id="qc-date"
                type="date"
                value={eventDate}
                onChange={(e) => setEventDate(e.target.value)}
                required
              />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-2">
                <Label htmlFor="qc-type">Event Type</Label>
                <select id="qc-type" title="Event Type" value={eventType} onChange={(e) => setEventType(e.target.value)} className={selectClass}>
                  <option value="match_day">Match Day</option>
                  <option value="practice">Practice</option>
                  <option value="tryout">Tryout</option>
                </select>
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="qc-sport">Sport</Label>
                <select id="qc-sport" title="Sport" value={sportId} onChange={(e) => setSportId(e.target.value)} className={selectClass}>
                  <option value="">No sport</option>
                  {schoolSports.map((s) => (
                    <option key={s.sport_id} value={s.sport_id}>{s.sport_name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-2">
                <Label htmlFor="qc-start">Start Time</Label>
                <Input id="qc-start" type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} required />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="qc-end">End Time</Label>
                <Input id="qc-end" type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} required />
              </div>
            </div>

            {/* Participating Schools */}
            <div className="flex flex-col gap-2">
              <Label>Participating Schools</Label>

              {/* Search box with dropdown */}
              <div ref={searchRef} className="relative">
                <div className="flex gap-2">
                  <Input
                    value={schoolSearch}
                    onChange={(e) => { setSchoolSearch(e.target.value); setShowDropdown(true); }}
                    onFocus={() => { if (schoolSearch.trim()) setShowDropdown(true); }}
                    placeholder="Search schools or type a name..."
                    className="h-9 text-sm"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        if (schoolSearchResults.length === 1) addDugadsSchool(schoolSearchResults[0]);
                      }
                      if (e.key === "Escape") setShowDropdown(false);
                    }}
                  />
                </div>

                {/* Dropdown results */}
                {showDropdown && schoolSearch.trim() && (
                  <div className="absolute left-0 right-0 top-full z-10 mt-1 max-h-48 overflow-y-auto rounded-md border border-input bg-background shadow-md">
                    {schoolSearchResults.map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => addDugadsSchool(s)}
                        className="flex w-full items-center px-3 py-2 text-left text-sm hover:bg-muted"
                      >
                        {s.name}
                      </button>
                    ))}
                    {schoolSearchLoading && (
                      <div className="px-3 py-2 text-sm text-muted-foreground">Searching...</div>
                    )}
                    {!schoolSearchLoading && schoolSearchResults.length === 0 && schoolSearch.trim().length >= 2 && (
                      <div className="px-3 py-2 text-sm text-muted-foreground">
                        No schools found for "{schoolSearch.trim()}"
                      </div>
                    )}
                    {!schoolSearchLoading && schoolSearch.trim().length < 2 && (
                      <div className="px-3 py-2 text-sm text-muted-foreground">
                        Type at least 2 characters to search
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Added schools as removable badges with host toggle */}
              {addedSchools.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {addedSchools.map((s, i) => (
                    <span
                      key={s.name}
                      className={`inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs font-medium ${
                        i === hostIndex
                          ? "border-primary bg-primary/15 text-primary"
                          : s.school_id
                            ? "border-primary/30 bg-primary/10 text-primary"
                            : "border-dashed border-input bg-muted text-muted-foreground"
                      }`}
                    >
                      <button
                        type="button"
                        title={i === hostIndex ? "Host school" : "Set as host"}
                        onClick={() => setHostIndex(i)}
                        className={`text-[10px] uppercase ${i === hostIndex ? "font-bold" : "opacity-50 hover:opacity-100"}`}
                      >
                        {i === hostIndex ? "Host" : "Host?"}
                      </button>
                      {s.name}
                      <button type="button" title={`Remove ${s.name}`} onClick={() => { removeAddedSchool(s.name); if (i <= hostIndex && hostIndex > 0) setHostIndex(hostIndex - 1); }} className="hover:text-destructive">
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Courts / Fields / Venues */}
            <div className="flex flex-col gap-2">
              <Label>{courtLabel}s</Label>
              <div ref={courtSearchRef} className="relative">
                <div className="flex gap-2">
                  <Input
                    value={courtSearch}
                    onChange={(e) => { setCourtSearch(e.target.value); setShowCourtDropdown(true); }}
                    onFocus={() => { if (courtSearch.trim()) setShowCourtDropdown(true); }}
                    placeholder={`Search ${courtLabel.toLowerCase()}s...`}
                    className="h-9 text-sm"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && filteredCourts.length === 1) { e.preventDefault(); addDugadsCourt(filteredCourts[0]); }
                      if (e.key === "Escape") setShowCourtDropdown(false);
                    }}
                  />
                </div>
                {showCourtDropdown && courtSearch.trim() && (
                  <div className="absolute left-0 right-0 top-full z-10 mt-1 max-h-48 overflow-y-auto rounded-md border border-input bg-background shadow-md">
                    {filteredCourts.map((c) => (
                      <button key={c.id} type="button" onClick={() => addDugadsCourt(c)} className="flex w-full items-center px-3 py-2 text-left text-sm hover:bg-muted">{c.name}</button>
                    ))}
                    {filteredCourts.length === 0 && (
                      <div className="px-3 py-2 text-sm text-muted-foreground">No matching {courtLabel.toLowerCase()}s</div>
                    )}
                  </div>
                )}
              </div>
              {addedCourts.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {addedCourts.map((c) => (
                    <span key={c.name} className={`inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs font-medium ${c.court_id ? "border-primary/30 bg-primary/10 text-primary" : "border-dashed border-input bg-muted text-muted-foreground"}`}>
                      {c.name}
                      <button type="button" title={`Remove ${c.name}`} onClick={() => removeAddedCourt(c.name)} className="hover:text-destructive"><X className="h-3 w-3" /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <Button type="submit" disabled={saving}>
                {saving ? "Creating..." : "Create Event"}
              </Button>
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

// --- Day View ---

function DayView({
  date,
  events,
  onAddEvent,
  onContextMenu,
  onSelectEvent,
  onEditEvent,
  onEventContextMenu,
}: {
  date: Date;
  events: CalendarEvent[];
  onAddEvent: (dateStr: string) => void;
  onContextMenu: (e: React.MouseEvent, dateStr: string) => void;
  onSelectEvent: (event: CalendarEvent) => void;
  onEditEvent: (event: CalendarEvent) => void;
  onEventContextMenu: (e: React.MouseEvent, event: CalendarEvent) => void;
}) {
  const dateStr = format(date, "yyyy-MM-dd");

  return (
    <div className="overflow-x-auto rounded-lg border border-border" data-region="calendar-grid">
      <div className="min-w-[400px]">
        {/* Hour grid */}
        {HOURS.map((hour) => {
          const hourEvents = events.filter((e) => {
            const h = parseISO(e.starts_at).getHours();
            return h === hour;
          });

          return (
            <div
              key={hour}
              className="flex min-h-[64px] border-b border-border"
              onContextMenu={(e) => onContextMenu(e, dateStr)}
            >
              <div className="w-16 shrink-0 border-r border-border px-2 py-1 text-right text-xs text-muted-foreground">
                {String(hour).padStart(2, "0")}:00
              </div>
              <DroppableDay
                dropId={`day-cell-${dateStr}-${hour}`}
                dateStr={dateStr}
                className={`flex flex-1 flex-col gap-1 p-1 ${hourEvents.length === 0 ? "cursor-pointer hover:bg-muted/50" : ""}`}
                onClick={() => { if (hourEvents.length === 0) onAddEvent(dateStr); }}
              >
                {hourEvents.map((event) => {
                  const colors = EVENT_COLORS[event.event_type] ?? EVENT_COLORS.match_day;
                  return (
                    <DraggableEventChip
                      key={event.id}
                      eventId={event.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectEvent(event);
                      }}
                      onDoubleClick={(e) => {
                        e.stopPropagation();
                        onEditEvent(event);
                      }}
                      onContextMenu={(e) => {
                        e.stopPropagation();
                        onEventContextMenu(e, event);
                      }}
                      className={`rounded border px-2 py-1 text-sm ${colors.bg} ${colors.text} ${colors.border} hover:opacity-80 cursor-grab active:cursor-grabbing`}
                    >
                      <div className="font-medium">{event.name}</div>
                      <div className="text-xs opacity-70">
                        {format(parseISO(event.starts_at), "HH:mm")} -{" "}
                        {format(parseISO(event.ends_at), "HH:mm")}
                        {event.courts?.length > 0 && ` | ${[...event.courts].sort((a, b) => a.name.localeCompare(b.name)).map(c => c.name).join(", ")}`}
                      </div>
                      <div className="text-xs opacity-70">
                        {event.match_count} {event.match_count === 1 ? "match" : "matches"}
                      </div>
                    </DraggableEventChip>
                  );
                })}
              </DroppableDay>
            </div>
          );
        })}

        {events.length === 0 && (
          <div className="flex flex-col items-center gap-2 py-12">
            <p className="text-muted-foreground">No events on this day</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onAddEvent(dateStr)}
            >
              <Plus className="mr-2 h-4 w-4" />
              Add Event
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function ListView({
  events,
  selectedEvent,
  onSelectEvent,
}: {
  events: CalendarEvent[];
  selectedEvent: CalendarEvent | null;
  onSelectEvent: (event: CalendarEvent) => void;
}) {
  const navigate = useNavigate();
  // Sort chronologically and group by date
  const sorted = [...events].sort(
    (a, b) => new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime(),
  );

  const grouped: { label: string; events: CalendarEvent[] }[] = [];
  let currentLabel = "";
  for (const ev of sorted) {
    const d = parseISO(ev.starts_at);
    const label = format(d, "EEEE, d MMMM yyyy");
    if (label !== currentLabel) {
      grouped.push({ label, events: [] });
      currentLabel = label;
    }
    grouped[grouped.length - 1].events.push(ev);
  }

  if (sorted.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 py-12">
        <CalendarIcon className="h-10 w-10 text-muted-foreground/50" />
        <p className="text-muted-foreground">No events match the current filters</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-region="list-view">
      {grouped.map((group) => (
        <div key={group.label}>
          <h3 className="mb-2 text-sm font-semibold text-muted-foreground">{group.label}</h3>
          <div className="space-y-2">
            {group.events.map((event) => {
              const colors = EVENT_COLORS[event.event_type] ?? EVENT_COLORS.match_day;
              const isSelected = selectedEvent?.id === event.id;
              const d = parseISO(event.starts_at);
              const typeLabel: Record<string, string> = {
                match_day: "Match Day",
                practice: "Practice",
                tryout: "Tryout",
              };

              return (
                <div
                  key={event.id}
                  onClick={() => onSelectEvent(event)}
                  onDoubleClick={() => navigate(`/events/${event.id}`)}
                  className={`flex cursor-pointer items-start gap-4 rounded-lg border bg-background p-4 transition-all hover:shadow-sm ${
                    isSelected
                      ? "border-primary ring-2 ring-primary/20"
                      : "border-border hover:border-border/80"
                  }`}
                >
                  {/* Date block */}
                  <div className="flex w-12 flex-shrink-0 flex-col items-center">
                    <span className="text-2xl font-bold leading-none">{format(d, "d")}</span>
                    <span className="text-xs font-medium uppercase text-muted-foreground">
                      {format(d, "EEE")}
                    </span>
                  </div>

                  {/* Event info */}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <div className="font-semibold leading-tight">{event.name}</div>
                      <EventStatusBadge event={event} />
                    </div>
                    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {format(d, "HH:mm")} - {format(parseISO(event.ends_at), "HH:mm")}
                      </span>
                      {event.courts?.length > 0 && (
                        <span className="flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {[...event.courts].sort((a, b) => a.name.localeCompare(b.name)).map(c => c.name).join(", ")}
                        </span>
                      )}
                      {event.sport_name && (
                        <span>{event.sport_name}</span>
                      )}
                      {event.match_count > 0 && (
                        <span>
                          {event.match_count} {event.match_count === 1 ? "match" : "matches"}
                        </span>
                      )}
                    </div>
                    {event.schools && event.schools.length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        {event.schools.map((s) => (
                          <span
                            key={s.id}
                            className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs ${
                              s.school_id === event.school_id
                                ? "bg-primary/10 font-medium text-primary"
                                : "bg-muted text-muted-foreground"
                            }`}
                          >
                            {s.name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex flex-shrink-0 flex-col items-end gap-1.5">
                    <span
                      className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${colors.bg} ${colors.text} ${colors.border}`}
                    >
                      {typeLabel[event.event_type] ?? event.event_type}
                    </span>
                    {event._actions.includes("send_message") && (
                      <div onClick={(e) => e.stopPropagation()}>
                        <SendMessageButton
                          contextType="event"
                          contextId={event.id}
                          contextLabel={event.name}
                          variant="ghost"
                        />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
